import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import clsx from 'clsx'
import { intl } from 'src/i18n'
import { socialTypes } from 'src/consts'
import { LiquidityComparison } from 'src/components/LiquidityComparison'
import Colors from 'src/components/TreeMap/components/Colors'
import useWidth from 'src/hooks/useWidth'
import { formatChangeValue } from 'src/utils/number/number'
import { formatCurrency2String } from 'src/utils/number/currency'
import { simplifyUrl } from 'src/utils/url'
import styles from './styles.module.scss'

const SymbolPriceChange = ({ change, change_usd: changeUsd, isMobile }) => {
  const colors = new Colors().createScale()
  const percentPriceChange = formatChangeValue(change, false)
  return (
    <div
      className={styles.priceChange}
      style={{
        color: colors(Math.round(change * 100) / 100),
      }}
    >
      {percentPriceChange
        ? `${changeUsd === '?' ? '?' : percentPriceChange}% ${String(
            changeUsd === '?'
              ? ''
              : `( ${`${isMobile ? '$' : ''}${formatCurrency2String(
                  changeUsd
                )}${!isMobile ? ' USD' : ''}`} )`
          ).replace('-', '')}`
        : ''}
    </div>
  )
}

export const InfoTable = ({ data }) => {
  const { isMobile } = useWidth()

  const {
    name,
    marketCapRank,
    extra,
    price,
    marketCap,
    volume,
    emission,
    totalSupply,
    yesterdayData,
    code,
  } = data
  const miningStats = extra ? extra.miningStats : null

  let renderMiningStats = false
  const { socials, explorers } = extra
  const website = socials?.filter(element => element.type === 'website')
  const whitepaper = socials?.filter(element => element.type === 'whitepaper')
  const github = socials?.find(element => element.type === 'github')
  const socialLinks = socials?.filter(el => socialTypes.includes(el.type))

  if (
    miningStats &&
    (Boolean(miningStats.algo) ||
      Boolean(miningStats.pools) ||
      Boolean(miningStats.pools_hash) ||
      Boolean(miningStats.network_hash))
  ) {
    renderMiningStats = true
  }

  return (
    <div className={styles.infoBlockContainer}>
      <table className={styles.infoBlock}>
        <tbody>
          <tr className={styles.infoBlockItem}>
            <td className={styles.infoBlockName}>
              {intl.t('coinDetails.infoTable.infoBlockName1', {
                name,
              })}
            </td>
            <td className={styles.infoBlockValue}>{`${
              isMobile ? '$' : ''
            }${formatCurrency2String(price)} ${!isMobile ? 'USD' : ''}`}</td>
          </tr>
          <tr className={styles.infoBlockItem}>
            <td className={styles.infoBlockName}>
              {intl.t('coinDetails.infoTable.infoBlockName2')}
            </td>
            <td className={styles.infoBlockValue}>#{marketCapRank}</td>
          </tr>
          <tr className={styles.infoBlockItem}>
            <td className={styles.infoBlockName}>
              {intl.t('coinDetails.infoTable.infoBlockName3')}
            </td>
            <td className={styles.infoBlockValue}>{`${
              isMobile ? '$' : ''
            }${formatCurrency2String(marketCap, false, '0,0')} ${
              !isMobile ? 'USD' : ''
            }`}</td>
          </tr>
          <tr className={styles.infoBlockItem}>
            <td className={styles.infoBlockName}>
              {intl.t('coinDetails.infoTable.infoBlockName4')}
            </td>
            <td className={styles.infoBlockValue}>{`${
              isMobile ? '$' : ''
            }${formatCurrency2String(volume, false, '0,0')} ${
              !isMobile ? 'USD' : ''
            }`}</td>
          </tr>
          <tr className={styles.infoBlockItem}>
            <td className={styles.infoBlockName}>
              {intl.t('coinDetails.infoTable.infoBlockName5')}
            </td>
            <td className={styles.infoBlockValue}>{`${formatCurrency2String(
              emission
            )} ${code}`}</td>
          </tr>
          <tr className={styles.infoBlockItem}>
            <td className={styles.infoBlockName}>
              {intl.t('coinDetails.infoTable.infoBlockName6')}
            </td>
            <td className={styles.infoBlockValue}>
              {totalSupply === 0
                ? intl.t('noData')
                : `${formatCurrency2String(totalSupply)} ${code}`}
            </td>
          </tr>
          {yesterdayData && (
            <>
              <tr className={clsx(styles.infoBlockItem, styles.space)}>
                <td colSpan='2' />
              </tr>
              <tr className={styles.infoBlockItem}>
                <td className={styles.infoBlockName}>
                  {isMobile
                    ? intl.t('coinDetails.infoTable.infoBlockName7.mobile')
                    : intl.t('coinDetails.infoTable.infoBlockName7.desktop')}
                </td>
                <td className={styles.infoBlockValue}>{`${
                  isMobile ? '$' : ''
                }${formatCurrency2String(yesterdayData.marketCap)} ${
                  !isMobile ? 'USD' : ''
                }`}</td>
              </tr>
              {!isMobile && (
                <>
                  <tr className={styles.infoBlockItem}>
                    <td className={styles.infoBlockName}>
                      {intl.t('coinDetails.infoTable.infoBlockName8')}
                    </td>
                    <td className={styles.infoBlockValue}>{`${
                      isMobile ? '$' : ''
                    }${formatCurrency2String(yesterdayData.open)} USD / ${
                      isMobile ? '$' : ''
                    }${formatCurrency2String(yesterdayData.close)} USD`}</td>
                  </tr>
                  <tr className={styles.infoBlockItem}>
                    <td className={styles.infoBlockName}>
                      {intl.t('coinDetails.infoTable.infoBlockName9')}
                    </td>
                    <td className={styles.infoBlockValue}>{`${
                      isMobile ? '$' : ''
                    }${formatCurrency2String(yesterdayData.high)} USD / ${
                      isMobile ? '$' : ''
                    }${formatCurrency2String(yesterdayData.low)} USD`}</td>
                  </tr>
                </>
              )}
              {isMobile && (
                <>
                  <tr className={styles.infoBlockItem}>
                    <td className={styles.infoBlockName}>
                      {intl.t('coinDetails.infoTable.infoBlockName10')}
                    </td>
                    <td
                      className={styles.infoBlockValue}
                    >{`$${formatCurrency2String(yesterdayData.open)}`}</td>
                  </tr>
                  <tr className={styles.infoBlockItem}>
                    <td className={styles.infoBlockName}>
                      {intl.t('coinDetails.infoTable.infoBlockName11')}
                    </td>
                    <td
                      className={styles.infoBlockValue}
                    >{`$${formatCurrency2String(yesterdayData.high)}`}</td>
                  </tr>
                  <tr className={styles.infoBlockItem}>
                    <td className={styles.infoBlockName}>
                      {intl.t('coinDetails.infoTable.infoBlockName12')}
                    </td>
                    <td
                      className={styles.infoBlockValue}
                    >{`$${formatCurrency2String(yesterdayData.low)}`}</td>
                  </tr>
                  <tr className={styles.infoBlockItem}>
                    <td className={styles.infoBlockName}>
                      {intl.t('coinDetails.infoTable.infoBlockName13')}
                    </td>
                    <td
                      className={styles.infoBlockValue}
                    >{`$${formatCurrency2String(yesterdayData.close)}`}</td>
                  </tr>
                </>
              )}
              <tr className={styles.infoBlockItem}>
                <td className={styles.infoBlockName}>
                  {intl.t('coinDetails.infoTable.infoBlockName14')}
                </td>
                <td className={styles.infoBlockValue}>
                  <SymbolPriceChange
                    change={
                      yesterdayData.change === 0 ? '?' : yesterdayData.change
                    }
                    change_usd={
                      yesterdayData.change_usd === 0
                        ? '?'
                        : yesterdayData.change_usd
                    }
                    isMobile={isMobile}
                  />
                </td>
              </tr>
              <tr className={styles.infoBlockItem}>
                <td className={styles.infoBlockName}>
                  {intl.t('coinDetails.infoTable.infoBlockName15')}
                </td>
                <td className={styles.infoBlockValue}>{`${
                  isMobile ? '$' : ''
                }${formatCurrency2String(yesterdayData.volume)} ${
                  !isMobile ? 'USD' : ''
                }`}</td>
              </tr>
            </>
          )}
          {renderMiningStats && (
            <>
              <tr className={clsx(styles.infoBlockItem, styles.space)}>
                <td colSpan='2' />
              </tr>
              <tr className={clsx(styles.infoBlockItem, styles.title)}>
                <td colSpan='2'>{intl.t('coinDetails.infoTable.mining')}</td>
              </tr>
              <tr className={styles.infoBlockItem}>
                <td className={styles.infoBlockName}>
                  {intl.t('coinDetails.infoTable.infoBlockName16')}
                </td>
                <td className={styles.infoBlockValue}>
                  {Boolean(miningStats.algo) ? miningStats.algo : '?'}
                </td>
              </tr>
              <tr className={styles.infoBlockItem}>
                <td className={styles.infoBlockName}>
                  {intl.t('coinDetails.infoTable.infoBlockName17')}
                </td>
                <td className={styles.infoBlockValue}>
                  {Boolean(miningStats.pools) ? miningStats.pools : '?'}
                </td>
              </tr>
              <tr className={styles.infoBlockItem}>
                <td className={styles.infoBlockName}>
                  {intl.t('coinDetails.infoTable.infoBlockName18')}
                </td>
                <td className={styles.infoBlockValue}>
                  {Boolean(miningStats.pools_hash)
                    ? miningStats.pools_hash
                    : '?'}
                </td>
              </tr>
              <tr className={styles.infoBlockItem}>
                <td className={styles.infoBlockName}>
                  {intl.t('coinDetails.infoTable.infoBlockName19')}
                </td>
                <td className={styles.infoBlockValue}>
                  {Boolean(miningStats.network_hash)
                    ? miningStats.network_hash
                    : '?'}
                </td>
              </tr>
              <tr className={clsx(styles.infoBlockItem, styles.partner)}>
                <td colSpan='2'>
                  <Link
                    href='https://miningpoolstats.stream/?utm_source=coin360&utm_medium=refferal&utm_campaign=miningpoolstats'
                    prefetch={false}
                    legacyBehavior
                  >
                    <a
                      className={styles.miningLink}
                      target='_blank'
                      rel='noopener noreferrer nofollow'
                    >
                      By MiningPoolStats
                      <div className={styles.img}>
                        <Image
                          src={'/assets/icons/icon-mining-pool-stats.svg'}
                          alt='MiningPoolStats'
                          width={23}
                          height={22}
                        />
                        <Image
                          src={'/assets/icons/icon-rectangle.svg'}
                          alt='Visit'
                          width={14}
                          height={14}
                        />
                      </div>
                    </a>
                  </Link>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      <LiquidityComparison coin={data} />
      <div className={styles.container}>
        {website && website.length > 0 && (
          <div className={styles.item}>
            <div className={styles.linkLabels}>
              {intl.t('coinDetails.infoTable.website')}
            </div>
            <div className={clsx(styles.links, styles.website)}>
              {website.map(element => {
                return (
                  <Link
                    href={element.link}
                    key={element.link}
                    prefetch={false}
                    legacyBehavior
                  >
                    <a
                      className={styles.linkElement}
                      target='_blank'
                      rel='noopener noreferrer nofollow'
                    >
                      {simplifyUrl(element.link)}
                    </a>
                  </Link>
                )
              })}
            </div>
          </div>
        )}
        {explorers?.length > 0 && (
          <div className={styles.item}>
            <div className={styles.linkLabels}>
              {intl.t('coinDetails.infoTable.explorers')}
            </div>
            <div className={clsx(styles.links, styles.column)}>
              {explorers.map((el, i) => (
                <Link key={i} href={el} prefetch={false} legacyBehavior>
                  <a
                    className={styles.linkElement}
                    target='_blank'
                    rel='noopener noreferrer nofollow'
                  >
                    {simplifyUrl(el)}
                  </a>
                </Link>
              ))}
            </div>
          </div>
        )}
        {socialLinks?.length > 0 && (
          <div className={styles.item}>
            <div className={styles.linkLabels}>
              {intl.t('coinDetails.infoTable.community')}
            </div>
            <div className={styles.links}>
              {socialLinks.map(el => (
                <Link
                  key={el.type}
                  href={el.link}
                  prefetch={false}
                  legacyBehavior
                >
                  <a
                    className={clsx(styles.communityItem, styles[el.type])}
                    target='_blank'
                    rel='noopener noreferrer nofollow'
                  >
                    <Image
                      src={`/assets/social/icon-${el.type}-white.svg`}
                      width='19'
                      height='19'
                      alt={`${el.type} icon`}
                    />
                  </a>
                </Link>
              ))}
            </div>
          </div>
        )}
        {github && (
          <div className={styles.item}>
            <div className={styles.linkLabels}>
              {intl.t('coinDetails.infoTable.sourceCode')}
            </div>
            <div className={styles.links}>
              <Link href={github.link} prefetch={false} legacyBehavior>
                <a
                  className={styles.linkElement}
                  target='_blank'
                  rel='noopener noreferrer nofollow'
                >
                  GitHub
                </a>
              </Link>
            </div>
          </div>
        )}
        {whitepaper && whitepaper.length > 0 && (
          <div className={styles.item}>
            <div className={styles.linkLabels}>
              {intl.t('coinDetails.infoTable.whitepaper')}
            </div>
            <div className={clsx(styles.links, styles.website)}>
              {whitepaper.map(element => {
                return (
                  <Link
                    href={element.link}
                    key={element.link}
                    prefetch={false}
                    legacyBehavior
                  >
                    <a
                      className={styles.linkElement}
                      target='_blank'
                      rel='noopener noreferrer nofollow'
                    >
                      {simplifyUrl(element.link)}
                    </a>
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
